import { useState, useEffect } from 'react';
import { X, Shield, BarChart, Megaphone } from 'lucide-react';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
  version: string;
}

interface CookieConsentProps {
  children: React.ReactNode;
}

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
    gtmLoaded?: boolean;
    gptEngLoaded?: boolean;
  }

  interface Navigator {
    globalPrivacyControl?: boolean;
  }
}

const CONSENT_KEY = 'xenira-cookie-consent';
const CONSENT_VERSION = '1.0';
const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

export const CookieConsent = ({ children }: CookieConsentProps) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    timestamp: Date.now(),
    version: CONSENT_VERSION,
  });

  // Check for Global Privacy Control or Do Not Track
  const checkGPC = (): boolean => {
    return (
      navigator.globalPrivacyControl === true ||
      navigator.doNotTrack === '1' ||
      (window.navigator as any).doNotTrack === '1'
    );
  };

  // Load Google Analytics
  const loadAnalytics = () => {
    if (window.gtmLoaded) return;
    if (checkGPC()) {
      return;
    }

    try {
      window.dataLayer = window.dataLayer || [];

      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=G-V4VE4JDTQ5';

      script.onload = () => {
        function gtag(...args: any[]) {
          window.dataLayer?.push(args);
        }
        window.gtag = gtag;
        gtag('js', new Date());
        gtag('config', 'G-V4VE4JDTQ5');
        window.gtmLoaded = true;

        gtag('consent', 'update', {
          analytics_storage: 'granted',
        });
      };

      script.onerror = () => {
        // Analytics loading failed silently
      };

      document.head.appendChild(script);
    } catch (error) {
      // Analytics loading error handled silently
    }
  };

  // Load Marketing Scripts
  const loadMarketing = () => {
    if (window.gptEngLoaded) return;
    if (checkGPC()) {
      return;
    }

    try {
      const script = document.createElement('script');
      script.src = 'https://cdn.gpteng.co/gptengineer.js';
      script.async = true;
      script.dataset.gptengSiteId = '1751292792927';

      script.onload = () => {
        window.gptEngLoaded = true;

        if (window.gtag) {
          window.gtag('consent', 'update', {
            ad_storage: 'granted',
            ad_user_data: 'granted',
            ad_personalization: 'granted',
          });
        }
      };

      script.onerror = () => {
        // Marketing script loading failed silently
      };

      document.body.appendChild(script);
    } catch (error) {
      // Marketing script loading error handled silently
    }
  };

  // Save consent to localStorage
  const saveConsent = (prefs: CookiePreferences) => {
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(prefs));
    } catch (error) {
      // Storage error handled silently
    }
  };

  // Handle Accept All
  const handleAcceptAll = () => {
    const newPrefs: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now(),
      version: CONSENT_VERSION,
    };

    setPreferences(newPrefs);
    saveConsent(newPrefs);
    loadAnalytics();
    loadMarketing();
    setShowBanner(false);
    setShowModal(false);
  };

  // Handle Reject Non-Essential
  const handleRejectNonEssential = () => {
    const newPrefs: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: Date.now(),
      version: CONSENT_VERSION,
    };

    setPreferences(newPrefs);
    saveConsent(newPrefs);
    setShowBanner(false);
    setShowModal(false);
  };

  // Handle Save Preferences
  const handleSavePreferences = () => {
    const newPrefs = {
      ...preferences,
      timestamp: Date.now(),
      version: CONSENT_VERSION,
    };

    saveConsent(newPrefs);
    if (newPrefs.analytics) loadAnalytics();
    if (newPrefs.marketing) loadMarketing();
    setShowModal(false);
    setShowBanner(false);
  };

  // Check for stored consent on mount
  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);

    if (stored) {
      try {
        const consent: CookiePreferences = JSON.parse(stored);

        // Check expiry (1 year)
        const isExpired = Date.now() - consent.timestamp > ONE_YEAR_MS;

        if (isExpired) {
          setShowBanner(true);
          localStorage.removeItem(CONSENT_KEY);
        } else {
          setPreferences(consent);
          if (consent.analytics) loadAnalytics();
          if (consent.marketing) loadMarketing();
        }
      } catch (error) {
        // Invalid consent data - show banner
        setShowBanner(true);
      }
    } else if (checkGPC()) {
      // GPC detected - auto-reject non-essential
      const gpcConsent: CookiePreferences = {
        necessary: true,
        analytics: false,
        marketing: false,
        timestamp: Date.now(),
        version: CONSENT_VERSION,
      };
      saveConsent(gpcConsent);
    } else {
      // No consent - show banner
      setShowBanner(true);
    }
  }, []);

  // Listen for footer "Cookie Settings" click
  useEffect(() => {
    const handleOpenPreferences = () => {
      setShowModal(true);
    };

    window.addEventListener('open-cookie-preferences', handleOpenPreferences);
    return () => {
      window.removeEventListener('open-cookie-preferences', handleOpenPreferences);
    };
  }, []);

  return (
    <>
      {children}

      {/* Cookie Banner */}
      {showBanner && !checkGPC() && (
        <div className="fixed bottom-0 left-0 right-0 z-[9999] animate-slide-up-fade">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 bg-background shadow-[0_-4px_20px_rgba(0,0,0,0.1)] rounded-t-2xl border-t border-border">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
              {/* Cookie Icon */}
              <div className="shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary/80 rounded-full flex items-center justify-center shadow-lg">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-primary-foreground"
                  >
                    <circle cx="12" cy="12" r="10" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5"/>
                    <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                    <circle cx="15" cy="7" r="1" fill="currentColor"/>
                    <circle cx="9" cy="14" r="1" fill="currentColor"/>
                    <circle cx="15" cy="13" r="1.5" fill="currentColor"/>
                    <circle cx="12" cy="17" r="1" fill="currentColor"/>
                    <path d="M12 2C11.5 2 11 2.1 10.5 2.3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
              </div>

              {/* Text Content */}
              <div className="flex-1 space-y-2">
                <h3 className="text-lg font-semibold text-foreground">
                  We value your privacy
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  We use cookies to enhance your experience, analyze traffic, and personalize content. You can customize your preferences or accept all.{' '}
                  <a href="/privacy-policy" className="text-primary hover:text-primary/80 underline transition-colors">
                    Learn more about our privacy policy
                  </a>
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto shrink-0">
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-3 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground rounded-lg font-semibold hover:from-primary/90 hover:to-primary/70 transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  Accept All
                </button>
                <button
                  onClick={handleRejectNonEssential}
                  className="px-6 py-3 bg-muted text-muted-foreground rounded-lg font-semibold hover:bg-muted/80 transition-all duration-200"
                >
                  Reject Non-Essential
                </button>
                <button
                  onClick={() => setShowModal(true)}
                  className="px-6 py-3 text-primary font-semibold hover:text-primary/80 underline transition-all duration-200"
                >
                  Customize
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Preferences Modal */}
      {showModal && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10000] animate-in fade-in duration-200"
            onClick={() => setShowModal(false)}
            aria-hidden="true"
          />

          {/* Modal */}
          <div
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl bg-background rounded-2xl shadow-2xl p-8 max-h-[80vh] overflow-y-auto z-[10001] animate-in zoom-in-95 duration-200"
            role="dialog"
            aria-labelledby="cookie-modal-title"
            aria-modal="true"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 id="cookie-modal-title" className="text-2xl font-bold text-foreground">
                  Cookie Preferences
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose which cookies you'd like to allow
                </p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Cookie Categories */}
            <div className="space-y-6">
              {/* Essential Cookies (locked) */}
              <div className="border border-border rounded-lg p-4 bg-card">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold text-card-foreground">Essential Cookies</h3>
                  </div>
                  <div className="relative w-14 h-8 rounded-full bg-primary cursor-not-allowed opacity-50">
                    <span className="absolute top-1 right-1 w-6 h-6 bg-background rounded-full shadow" />
                  </div>
                </div>
                <p className="text-sm text-muted-foreground ml-8">
                  Required for the website to function properly. These cookies cannot be disabled.
                </p>
              </div>

              {/* Analytics Cookies */}
              <div className="border border-border rounded-lg p-4 bg-card">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <BarChart className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold text-card-foreground">Analytics Cookies</h3>
                  </div>
                  <button
                    onClick={() => setPreferences((prev) => ({ ...prev, analytics: !prev.analytics }))}
                    className={`relative w-14 h-8 rounded-full transition-colors ${
                      preferences.analytics ? 'bg-primary' : 'bg-muted'
                    }`}
                    aria-label={`Analytics cookies ${preferences.analytics ? 'enabled' : 'disabled'}`}
                    aria-pressed={preferences.analytics}
                  >
                    <span
                      className={`absolute top-1 left-1 w-6 h-6 bg-background rounded-full shadow transition-transform ${
                        preferences.analytics ? 'translate-x-6' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
                <p className="text-sm text-muted-foreground ml-8 mb-2">
                  Help us understand how visitors interact with our website by collecting anonymous data.
                </p>
                <p className="text-xs text-muted-foreground ml-8">Tools: Google Analytics</p>
              </div>

              {/* Marketing Cookies */}
              <div className="border border-border rounded-lg p-4 bg-card">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Megaphone className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold text-card-foreground">Marketing Cookies</h3>
                  </div>
                  <button
                    onClick={() => setPreferences((prev) => ({ ...prev, marketing: !prev.marketing }))}
                    className={`relative w-14 h-8 rounded-full transition-colors ${
                      preferences.marketing ? 'bg-primary' : 'bg-muted'
                    }`}
                    aria-label={`Marketing cookies ${preferences.marketing ? 'enabled' : 'disabled'}`}
                    aria-pressed={preferences.marketing}
                  >
                    <span
                      className={`absolute top-1 left-1 w-6 h-6 bg-background rounded-full shadow transition-transform ${
                        preferences.marketing ? 'translate-x-6' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
                <p className="text-sm text-muted-foreground ml-8 mb-2">
                  Used for personalization and to provide you with relevant content.
                </p>
                <p className="text-xs text-muted-foreground ml-8">Tools: Marketing trackers</p>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t border-border">
              <button
                onClick={handleSavePreferences}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground rounded-lg font-semibold hover:from-primary/90 hover:to-primary/70 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Save Preferences
              </button>
              <button
                onClick={handleAcceptAll}
                className="flex-1 px-6 py-3 bg-muted text-muted-foreground rounded-lg font-semibold hover:bg-muted/80 transition-all duration-200"
              >
                Accept All
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};
